/**
 * Types metier partages entre l'API, les workers et le frontend.
 * Aucune dependance runtime : ce fichier decrit le vocabulaire du domaine.
 */

/** Statut administratif de l'etablissement tel que renvoye par SIRENE. */
export const STATUTS_SIRENE = ['ACTIF', 'CESSE'] as const;
export type StatutSirene = (typeof STATUTS_SIRENE)[number];

/** Avancement de l'enrichissement multi-sources d'une fiche lead. */
export const STATUTS_ENRICHISSEMENT = [
  'EN_ATTENTE',
  'PARTIEL',
  'COMPLET',
  'MANUEL_REQUIS',
  'ECHEC',
] as const;
export type StatutEnrichissement = (typeof STATUTS_ENRICHISSEMENT)[number];

export const LIBELLES_ENRICHISSEMENT: Record<StatutEnrichissement, string> = {
  EN_ATTENTE: 'En attente',
  PARTIEL: 'Partiel',
  COMPLET: 'Complet',
  MANUEL_REQUIS: 'À compléter',
  ECHEC: 'Échec',
};

/** Provenance d'une donnee dirigeant. */
export const SOURCES_DIRIGEANT = ['INPI', 'MANUEL', 'LINKEDIN_MANUEL'] as const;
export type SourceDirigeant = (typeof SOURCES_DIRIGEANT)[number];

/** Nature d'une interaction commerciale saisie par l'utilisateur. */
export const TYPES_INTERACTION = ['APPEL', 'EMAIL', 'NOTE', 'RDV', 'DEMO', 'AUTRE'] as const;
export type TypeInteraction = (typeof TYPES_INTERACTION)[number];

export const LIBELLES_INTERACTION: Record<TypeInteraction, string> = {
  APPEL: 'Appel',
  EMAIL: 'E-mail',
  NOTE: 'Note',
  RDV: 'Rendez-vous',
  DEMO: 'Démo',
  AUTRE: 'Autre',
};

/**
 * Interactions qui supposent d'avoir joint le prospect : elles repoussent la
 * duree de conservation RGPD et sont refusees sur un opposant.
 */
export const TYPES_INTERACTION_CONTACT = ['APPEL', 'EMAIL', 'RDV', 'DEMO'] as const satisfies readonly TypeInteraction[];

export function estInteractionContact(type: TypeInteraction): boolean {
  return (TYPES_INTERACTION_CONTACT as readonly TypeInteraction[]).includes(type);
}

/**
 * Contact avec le prospect, quel que soit le type de l'interaction : un type de
 * contact, ou un `resultat` (issue d'un echange, ou d'une tentative) -- une
 * note qui consigne "Joint" rapporte un echange comme un appel. Sert au refus
 * sur un opposant (409 `LEAD_OPPOSANT`) : toute tentative, aboutie ou non,
 * sollicite la personne et est donc refusee. Ne repousse plus, a lui seul, la
 * conservation RGPD -- voir `estEchangeEffectif`, plus strict. Equivalent
 * Prisma pour les requetes : `WHERE_CONTACT_PROSPECT`
 * (`apps/api/src/modules/leads/filtres.ts`), dont un test sur base verifie
 * l'equivalence.
 */
export function estContactProspect(interaction: {
  type: TypeInteraction;
  resultat?: ResultatInteraction | null;
}): boolean {
  return estInteractionContact(interaction.type) || (interaction.resultat ?? null) !== null;
}

/**
 * Resultats d'une tentative restee sans reponse : la personne n'a pas ete
 * jointe, il n'y a donc rien a lui reprocher d'avoir "trop" ete sollicitee.
 */
export const RESULTATS_SANS_ECHANGE = ['NON_JOIGNABLE', 'REPONDEUR'] as const satisfies readonly ResultatInteraction[];

/**
 * Echange effectif avec le prospect (RGPD, conservation -- `docs/rgpd.md`
 * §5.1) : un contact (`estContactProspect`) dont le resultat, s'il y en a un,
 * n'est ni `NON_JOIGNABLE` ni `REPONDEUR`. Un appel ou un e-mail sans resultat
 * renseigne reste compte, comme avant ; un resultat `AUTRE` compte comme
 * effectif. C'est ce predicat, et lui seul, qui repousse `dernierContactLe`
 * (`apps/api/src/modules/leads/service.ts`, `creerInteraction`) -- le refus
 * sur un opposant reste base sur `estContactProspect`, inchange : une
 * tentative sans reponse sollicite quand meme la personne.
 */
export function estEchangeEffectif(interaction: {
  type: TypeInteraction;
  resultat?: ResultatInteraction | null;
}): boolean {
  if (!estContactProspect(interaction)) return false;
  const resultat = interaction.resultat ?? null;
  return resultat === null || !(RESULTATS_SANS_ECHANGE as readonly ResultatInteraction[]).includes(resultat);
}

/** Types admis pour une prochaine etape : une note ne se planifie pas. */
export const TYPES_PROCHAINE_ETAPE = ['APPEL', 'EMAIL', 'RDV', 'DEMO', 'AUTRE'] as const satisfies readonly TypeInteraction[];
export type TypeProchaineEtape = (typeof TYPES_PROCHAINE_ETAPE)[number];

/** Issue d'une interaction (surtout utile pour les appels). */
export const RESULTATS_INTERACTION = [
  'JOIGNABLE',
  'NON_JOIGNABLE',
  /** Message laisse sur le repondeur. */
  'REPONDEUR',
  'RAPPEL_DEMANDE',
  'INTERESSE',
  'PAS_INTERESSE',
  'RDV_FIXE',
  'AUTRE',
] as const;
export type ResultatInteraction = (typeof RESULTATS_INTERACTION)[number];

/** Libelles des issues d'interaction, tels que proposes a la saisie d'un appel. */
export const LIBELLES_RESULTAT: Record<ResultatInteraction, string> = {
  JOIGNABLE: 'Joint',
  NON_JOIGNABLE: 'Pas de réponse',
  REPONDEUR: 'Répondeur',
  RAPPEL_DEMANDE: 'À rappeler',
  INTERESSE: 'Intéressé',
  PAS_INTERESSE: 'Pas intéressé',
  RDV_FIXE: 'RDV fixé',
  AUTRE: 'Autre',
};

/** Sources de donnees externes utilisees par l'application. */
export const SOURCES_SYNC = ['SIRENE', 'INPI', 'GOOGLE_PLACES'] as const;
export type SourceSync = (typeof SOURCES_SYNC)[number];

export const STATUTS_SYNC = ['EN_COURS', 'SUCCES', 'ECHEC', 'ANNULE'] as const;
export type StatutSync = (typeof STATUTS_SYNC)[number];

/** Provenance d'un lead : import SIRENE ou creation manuelle a partir d'un SIRET. */
export const ORIGINES_LEAD = ['IMPORT', 'MANUEL'] as const;
export type OrigineLead = (typeof ORIGINES_LEAD)[number];

export const LIBELLES_ORIGINE_LEAD: Record<OrigineLead, string> = {
  IMPORT: 'Import SIRENE',
  MANUEL: 'Création manuelle',
};

/**
 * Roles applicatifs, du plus au moins etendu. Leurs droits se lisent dans
 * `PERMISSIONS_PAR_ROLE` (permissions.ts), jamais par comparaison directe.
 */
export const ROLES = ['ADMIN', 'MANAGER', 'COMMERCIAL', 'LECTEUR'] as const;
export type Role = (typeof ROLES)[number];

export const LIBELLES_ROLE: Record<Role, string> = {
  ADMIN: 'Administrateur',
  MANAGER: 'Manager',
  COMMERCIAL: 'Commercial',
  LECTEUR: 'Lecteur',
};

/** Une phrase par role, affichee au choix du role d'un compte. */
export const DESCRIPTIONS_ROLE: Record<Role, string> = {
  ADMIN: 'Accès complet : équipe, étapes du pipeline, intégrations, IA et suppression des leads.',
  MANAGER: "Pilote l'équipe : voit et modifie tous les leads, les attribue, importe et exporte.",
  COMMERCIAL: "Travaille ses leads et le vivier : un lead du vivier lui est attribué dès qu'il agit dessus.",
  LECTEUR: 'Consulte tous les leads et les statistiques, sans rien modifier.',
};

/**
 * Statuts commerciaux livres par defaut.
 * Ils sont inseres en base au seed et restent modifiables par l'utilisateur
 * (cahier des charges 3.3 : "statuts personnalisables").
 */
export interface StatutCommercialDefaut {
  code: string;
  libelle: string;
  couleur: string;
  ordre: number;
  /** Statut applique automatiquement a un lead nouvellement importe. */
  estInitial: boolean;
  /** Statut terminal : le lead sort du pipeline actif. */
  estFinal: boolean;
  /** Statut terminal positif, utilise pour le taux de conversion. */
  estGagne: boolean;
}

export const STATUTS_COMMERCIAUX_DEFAUT: readonly StatutCommercialDefaut[] = [
  {
    code: 'A_CONTACTER',
    libelle: 'À contacter',
    couleur: '#64748b',
    ordre: 10,
    estInitial: true,
    estFinal: false,
    estGagne: false,
  },
  {
    code: 'RELANCE',
    libelle: 'Relancé',
    couleur: '#0ea5e9',
    ordre: 20,
    estInitial: false,
    estFinal: false,
    estGagne: false,
  },
  {
    code: 'RDV_PRIS',
    libelle: 'RDV pris',
    couleur: '#8b5cf6',
    ordre: 30,
    estInitial: false,
    estFinal: false,
    estGagne: false,
  },
  {
    code: 'DEMO_FAITE',
    libelle: 'Démo faite',
    couleur: '#f59e0b',
    ordre: 40,
    estInitial: false,
    estFinal: false,
    estGagne: false,
  },
  {
    code: 'CONVERTI',
    libelle: 'Converti',
    couleur: '#16a34a',
    ordre: 50,
    estInitial: false,
    estFinal: true,
    estGagne: true,
  },
  {
    code: 'PERDU',
    libelle: 'Perdu',
    couleur: '#dc2626',
    ordre: 60,
    estInitial: false,
    estFinal: true,
    estGagne: false,
  },
] as const;

/**
 * Tranches d'effectif SIRENE (variable `trancheEffectifsEtablissement`).
 * La borne basse sert au filtre "taille effectif".
 */
export const TRANCHES_EFFECTIF: Record<string, { libelle: string; min: number; max: number | null }> = {
  NN: { libelle: 'Non renseigné', min: 0, max: null },
  '00': { libelle: '0 salarié', min: 0, max: 0 },
  '01': { libelle: '1 à 2 salariés', min: 1, max: 2 },
  '02': { libelle: '3 à 5 salariés', min: 3, max: 5 },
  '03': { libelle: '6 à 9 salariés', min: 6, max: 9 },
  '11': { libelle: '10 à 19 salariés', min: 10, max: 19 },
  '12': { libelle: '20 à 49 salariés', min: 20, max: 49 },
  '21': { libelle: '50 à 99 salariés', min: 50, max: 99 },
  '22': { libelle: '100 à 199 salariés', min: 100, max: 199 },
  '31': { libelle: '200 à 249 salariés', min: 200, max: 249 },
  '32': { libelle: '250 à 499 salariés', min: 250, max: 499 },
  '41': { libelle: '500 à 999 salariés', min: 500, max: 999 },
  // Milliers separes par une espace fine insecable (U+202F), comme les nombres
  // formates par l'interface (apps/web/src/lib/format.ts).
  '42': { libelle: '1 000 à 1 999 salariés', min: 1000, max: 1999 },
  '51': { libelle: '2 000 à 4 999 salariés', min: 2000, max: 4999 },
  '52': { libelle: '5 000 à 9 999 salariés', min: 5000, max: 9999 },
  '53': { libelle: '10 000 salariés et plus', min: 10000, max: null },
};

export function libelleTrancheEffectif(code: string | null | undefined): string {
  if (!code) return TRANCHES_EFFECTIF.NN!.libelle;
  return TRANCHES_EFFECTIF[code]?.libelle ?? TRANCHES_EFFECTIF.NN!.libelle;
}

export function effectifMinimum(code: string | null | undefined): number | null {
  if (!code) return null;
  return TRANCHES_EFFECTIF[code]?.min ?? null;
}

/**
 * Secteurs cibles. Le perimetre initial est l'enseignement de la conduite
 * (NAF 85.53Z) ; la structure reste ouverte au multi-secteurs (V3).
 */
export interface Secteur {
  code: string;
  libelle: string;
  codesNaf: string[];
  /** Secteur propose par defaut dans les ecrans d'import. */
  defaut: boolean;
}

export const SECTEURS: readonly Secteur[] = [
  {
    code: 'AUTO_ECOLE',
    libelle: 'Auto-écoles',
    codesNaf: ['85.53Z', '8553Z'],
    defaut: true,
  },
  {
    code: 'FORMATION',
    libelle: 'Autres formations',
    codesNaf: ['85.59A', '85.59B', '8559A', '8559B'],
    defaut: false,
  },
] as const;

export const NAF_AUTO_ECOLE = '85.53Z';

/** Normalise un code NAF ("8553Z" ou "85.53Z") vers la forme pointee. */
export function normaliserCodeNaf(code: string): string {
  const brut = code.trim().toUpperCase().replace(/[.\s]/g, '');
  if (brut.length !== 5) return code.trim().toUpperCase();
  return `${brut.slice(0, 2)}.${brut.slice(2)}`;
}

export function secteurPourNaf(codeNaf: string | null | undefined): Secteur | undefined {
  if (!codeNaf) return undefined;
  const normalise = normaliserCodeNaf(codeNaf);
  return SECTEURS.find((s) => s.codesNaf.map(normaliserCodeNaf).includes(normalise));
}

/**
 * Categorie juridique INSEE : code "personne physique".
 *
 * La documentation SIRENE precise que cette valeur est portee par toute
 * personne physique, "que la personne soit artisan, commercant, profession
 * liberale, agriculteur ou autre", et qu'elle ne change jamais. C'est donc le
 * discriminant entre un entrepreneur individuel et une societe.
 */
export const CATEGORIE_JURIDIQUE_PERSONNE_PHYSIQUE = '1000';

/**
 * Libelles des categories juridiques rencontrees sur le secteur.
 * Liste volontairement partielle : la nomenclature complete compte plusieurs
 * centaines de codes, un code absent est affiche tel quel.
 */
const LIBELLES_CATEGORIE_JURIDIQUE: Record<string, string> = {
  '1000': 'Entrepreneur individuel',
  '5202': 'Société en nom collectif',
  '5306': 'Société en commandite simple',
  '5426': 'SARL immobilière',
  '5498': 'SARL, autre',
  '5499': 'SARL',
  '5505': "SA à conseil d'administration",
  '5510': "SA à conseil d'administration",
  '5515': "SA à conseil d'administration",
  '5560': 'SA à directoire',
  '5599': 'SA',
  '5710': 'SAS',
  '5720': 'SASU',
  '5785': "Société d'exercice libéral par actions simplifiée",
  '6540': 'SCI',
  '6220': "Groupement d'intérêt économique",
  '9220': 'Association déclarée',
  '9260': 'Association de droit local',
};

export function libelleCategorieJuridique(code: string | null | undefined): string | null {
  if (!code) return null;
  return LIBELLES_CATEGORIE_JURIDIQUE[code.trim()] ?? code.trim();
}

export function estPersonnePhysique(code: string | null | undefined): boolean {
  return code?.trim() === CATEGORIE_JURIDIQUE_PERSONNE_PHYSIQUE;
}

/**
 * Nature de l'activite declaree au Registre National des Entreprises.
 *
 * Ce champ vient de l'INPI : SIRENE ne le porte pas. Il est independant de la
 * forme juridique -- une societe peut exercer en liberal -- et distingue une
 * auto-ecole exploitee comme un commerce d'un moniteur independant.
 *
 * La nomenclature n'est pas figee : une valeur inconnue est affichee telle
 * quelle plutot qu'ecartee.
 */
export const NATURES_ACTIVITE = [
  'COMMERCIALE',
  'ARTISANALE',
  'AGRICOLE',
  'LIBERALE_REGLEMENTEE',
  'LIBERALE_NON_REGLEMENTEE',
] as const;
export type NatureActivite = (typeof NATURES_ACTIVITE)[number];

const LIBELLES_NATURE_ACTIVITE: Record<string, string> = {
  COMMERCIALE: 'Commerciale',
  ARTISANALE: 'Artisanale',
  AGRICOLE: 'Agricole',
  LIBERALE_REGLEMENTEE: 'Libérale réglementée',
  LIBERALE_NON_REGLEMENTEE: 'Libérale non réglementée',
};

export function libelleNatureActivite(valeur: string | null | undefined): string | null {
  if (!valeur) return null;
  const code = valeur.trim().toUpperCase();
  return LIBELLES_NATURE_ACTIVITE[code] ?? valeur.trim();
}

export function estActiviteLiberale(valeur: string | null | undefined): boolean {
  return (valeur ?? '').trim().toUpperCase().startsWith('LIBERALE');
}
